import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import ShopHeader from "@/components/site/Nav";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { useEditions } from "@/context/EditionsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { refresh } = useEditions();
  const { t } = useLanguage();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          refresh(); // live edition counters reflect the sale immediately
          setState("paid");
          return;
        }
      } catch {
        // keep trying
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <ShopHeader solid />
      <main className="pt-40 pb-32">
        <div className="mx-auto max-w-[720px] px-6 text-center">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#a89f8e]" strokeWidth={1.5} />
              <p className="mt-6 font-light text-[#111111]/70">{t("success.checking")}</p>
            </>
          )}
          {state === "paid" && (
            <div data-testid="payment-success">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-[#111111]">
                <Check className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <h1 className="mt-8 font-display text-4xl md:text-5xl tracking-tight text-[#111111]">
                {t("success.title")}
              </h1>
              <p className="mt-6 font-light text-[#111111]/70">{t("success.body")}</p>
              <Link
                to="/shop"
                className="mt-10 inline-block border border-[#111111] px-7 py-4 text-sm font-light tracking-wide hover:bg-[#111111] hover:text-[#faf8f5] transition-colors duration-500"
              >
                {t("success.back")}
              </Link>
            </div>
          )}
          {(state === "timeout" || state === "error") && (
            <div data-testid="payment-pending">
              <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#111111]">
                {t("success.pending")}
              </h1>
              <p className="mt-6 font-light text-[#111111]/70">{t("success.pendingBody")}</p>
              <Link to="/shop" className="mt-8 inline-block link-underline">
                {t("success.back")}
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
